import { useEffect, useState } from "react";
import { tEnglish as t } from "@buza/i18n";
import { api } from "./api.js";
import { Profile } from "./profile/Profile.js";
import { Subscription } from "./subscription/Subscription.js";
import { ParlourShell, type ScreenDef } from "./shell/ParlourShell.js";
import { SignedInBar } from "./shell/SignedInBar.js";
import type { StaffSession } from "./signin/session.js";
import { SignIn } from "./signin/SignIn.js";
import { ForgotPassword } from "./password/ForgotPassword.js";
import { linkTokenFromHash, SetPassword } from "./password/SetPassword.js";

// Parlour app. An NS-03 link (#/set-password/{token}, S-008) opens the set-password page whatever
// the session; the token is taken out of the address bar at once. S01 sign-in (S-007) guards every
// other screen: without a staff session the app shows S01, or its Forgot password request; with one, the shell shows the signed-in user, sign-out and the screens built so far:
// S03 Profile and branding (S-043) and the S05 payment history (S-051). S02, S04, the rest of S05
// (S-056) and the Lite, CRM and remembrance screens add their routes here. Every phrase comes from
// a key (TRN-01).
const SCREENS: ScreenDef[] = [
  { id: "profile", label: "parlour.profile.nav", render: () => <Profile /> },
  // S-051 the S05 payment history (ST-067); the rest of S05 is S-056.
  { id: "subscription", label: "parlour.subscription.nav", render: () => <Subscription /> },
];

/** Reads a set-password link's token once and removes it from the address bar and history. */
function takeLinkToken(): { token: string | undefined } | undefined {
  if (!window.location.hash.startsWith("#/set-password")) return undefined;
  const token = linkTokenFromHash(window.location.hash);
  window.history.replaceState(null, "", window.location.pathname + window.location.search);
  return { token };
}

export function App() {
  const [session, setSession] = useState<StaffSession | null | undefined>(undefined);
  const [link, setLink] = useState(takeLinkToken);
  const [forgot, setForgot] = useState(false);

  useEffect(() => {
    document.title = t("parlour.shell.app_name");
    void api<StaffSession>("/v1/parlour/session").then((r) => setSession(r.ok ? r.data : null));
  }, []);

  if (link) return <SetPassword token={link.token} onDone={() => { setLink(undefined); setSession(null); }} />;
  if (session === undefined) return <main aria-busy="true" />;
  if (session === null) {
    if (forgot) return <ForgotPassword onBack={() => setForgot(false)} />;
    return <SignIn onSignedIn={setSession} onForgot={() => setForgot(true)} />;
  }

  async function signOut() {
    await api("/v1/parlour/session", { method: "DELETE" });
    setSession(null);
  }

  return <ParlourShell screens={SCREENS} header={<SignedInBar session={session} onSignOut={signOut} />} />;
}
